import IconCalendar from '../icons/IconCalendar';
import IconTicket from '../icons/IconTicket.jsx';
import IconSeat from '../icons/IconSeat.jsx';
import IconInfo from '../icons/IconInfo.jsx';
import ShowDatePicker from '../step1/ShowDatePicker.js';
import Accordion from './Accordion';
import SidebarDatepickerStatic from '../step1/SidebarDatepickerStatic';
import SidebarTickets from '../step2/SidebarTickets';
import SidebarSeats from '../step3/SidebarSeats';

import { useCart } from '../hooks/useCart.js';
import { useTranslation } from 'react-i18next';
import { isStep3Needed } from '../helpers/stepNavigation.js';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types.js';

// This component displays booking information in the sidebar of the widget.
// It shows the user's name, phone number, and email address based on the generic booking fields
// not used in the current widget flow, but can be useful for reference or future use.
function SidebarBookingInfo() {
    const { genericBookingFields } = useCart();
    const { t } = useTranslation();

    return (
        <div className="fireknife-booking-info">
            <div className="fireknife-booking-info__list">
                <div className="fireknife-booking-info__item">
                    <div className="fireknife-booking-info__item-title">
                        {t('widgetBookingInformation.name')}
                    </div>
                    <div className="fireknife-booking-info__item-desc">
                        {genericBookingFields?.firstName} {genericBookingFields?.lastName}
                    </div>
                </div>
                <div className="fireknife-booking-info__item">
                    <div className="fireknife-booking-info__item-title">
                        {t('widgetBookingInformation.phone')}
                    </div>
                    <div className="fireknife-booking-info__item-desc">
                        {genericBookingFields?.phone}
                    </div>
                </div>
                <div className="fireknife-booking-info__item">
                    <div className="fireknife-booking-info__item-title">
                        {t('widgetBookingInformation.email')}
                    </div>
                    <div className="fireknife-booking-info__item-desc">
                        {genericBookingFields?.email}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Steps({
    showId,
    children,
    step,
    setStep,
    delayedSeatAssignmentEnabled = false,
    forceShowSeatsStep = false,
    hideSeatsStep = false,
    rentalMode = false,
}) {
    const { t } = useTranslation();
    const { cart } = useCart();
    const { data: { ticketTypes } = { ticketTypes: [] } } = useGetTicketTypes({
        optionalId: showId,
        includeSeats: true,
        onlyShowInList: true,
        onlyGuestVisible: true,
    });

    const stepNeedsStep3 = isStep3Needed(cart, ticketTypes, delayedSeatAssignmentEnabled);

    const data = [
        {
            icon: <IconCalendar />,
            title: t('widgetSidebarSteps.datetime'),
            content: <ShowDatePicker showId={showId} location="aside" rentalMode={rentalMode} />,
            passiveContent: <SidebarDatepickerStatic />,
        },
        {
            icon: <IconTicket className="fireknife-accordion__summary-icon" />,
            title: t('widgetSidebarSteps.tickets'),
            content: <SidebarTickets />,
            passiveContent: <SidebarTickets />,
        },
        {
            icon: <IconSeat className="fireknife-accordion__summary-icon" />,
            title: t('widgetSidebarSteps.seats'),
            content: <SidebarSeats />,
            passiveContent: <SidebarSeats />,
        },
        {
            icon: <IconInfo className="fireknife-accordion__summary-icon" />,
            title: t('widgetSidebarSteps.bookingInformation'),
            content: null /*<SidebarBookingInfo />*/,
            passiveContent: null /*<SidebarBookingInfo />*/,
        },
    ];

    // Filter out Step 3 (seats) if not needed
    const shouldShowSeatsStep =
        hideSeatsStep === false && (forceShowSeatsStep || (step > 1 && stepNeedsStep3));
    const filteredData = shouldShowSeatsStep ? data : data.filter((item, idx) => idx !== 2);

    return (
        <div className="fireknife-steps">
            {filteredData.map((item, filteredIdx) => {
                // Calculate the logical step number, accounting for step 3 being filtered out
                const logicalStepIdx = shouldShowSeatsStep
                    ? filteredIdx
                    : filteredIdx >= 2
                      ? filteredIdx + 1
                      : filteredIdx;
                const stepNumber = logicalStepIdx + 1;
                const isOpen = step === stepNumber;
                const isCompletedStep = logicalStepIdx < step;

                return (
                    <Accordion
                        key={stepNumber}
                        icon={item.icon}
                        title={item.title}
                        open={true}
                        disabled={logicalStepIdx >= step}
                        onToggle={() => {
                            setStep(stepNumber);
                        }}
                    >
                        {isOpen && item.content}
                        {!isOpen && isCompletedStep && item.passiveContent}
                        {isOpen && children}
                    </Accordion>
                );
            })}
        </div>
    );
}

export default Steps;
